import { useContext, useEffect } from "react"

import { useTrans, Trans } from "@/i18n"
import { Button, ButtonLink } from "@/components/Button"
import { CheckIcon } from "@/lib/icons"
import {
	EXTERNAL_PLATFORM_AFM_LICENSE_URL,
	EXTERNAL_PLATFORM_ZONHUB_FAQ_URL,
} from "@/lib/env"
import { cn } from "@/lib/utils"
import { useQueryClient } from "@/lib/query"

import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/useToasts"
import { useCurrentUserKyc } from "@/context/user"

import {
	type IdentityVerificationStartMutation,
	type ScaEnrollmentStartMutation,
	RiskKycStateChoices,
	useIdentityVerificationStartMutation,
	useScaEnrollmentStartMutation,
	useUserKycStatusQuery,
} from "@/api/graphql"

import { useCurrentDate, useLocalizedDateTime, ms } from "@/lib/dates"
import { captureMessage } from "@/lib/sentry"

const SESSION_TTL_MS = ms("7d")
const HOSTED_POLL_INTERVAL_MS = ms("10s")

const HOSTED_STATUS = {
	PENDING: "PENDING",
	REVIEW: "REVIEW",
	VALIDATED: "VALIDATED",
	REFUSED: "REFUSED",
	EXPIRED: "EXPIRED",
	OUT_OF_DATE: "OUT_OF_DATE",
} as const

type HostedStatusKey = keyof typeof HOSTED_STATUS

const STATUSES_NEEDING_RETRY: Array<string> = [
	HOSTED_STATUS.REFUSED,
	HOSTED_STATUS.EXPIRED,
	HOSTED_STATUS.OUT_OF_DATE,
]

type KycStepStatus = "complete" | "current" | "upcoming"

interface KycVerificationStepProps {
	status: KycStepStatus
}

export const KycVerificationStep = ({ status }: KycVerificationStepProps) => {
	const t = useTrans("investor")
	const userKyc = useCurrentUserKyc()
	const usesHostedKyc = userKyc?.uses_hosted_verification ?? false

	let statusLabelKey: string
	if (status === "complete") {
		statusLabelKey =
			"investor:identity.copy.mangopay.timeline.status.complete"
	} else if (status === "current") {
		statusLabelKey =
			"investor:identity.copy.mangopay.timeline.status.current"
	} else {
		statusLabelKey =
			"investor:identity.copy.mangopay.timeline.status.upcoming"
	}
	const statusLabel = t(statusLabelKey)

	return (
		<li className="relative flex gap-4">
			<StepIndicator stepNumber={2} status={status} />

			<div className="flex-1 pt-0.5">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<h3
						className={cn(
							"text-sm font-semibold",
							status === "current"
								? "text-gray-900"
								: "text-gray-500",
						)}
					>
						{t(
							"investor:identity.copy.mangopay.timeline.kyc.title",
						)}
					</h3>
					<StatusPill status={status} label={statusLabel} />
				</div>
				<div
					className={cn(
						"mt-1 space-y-3 text-sm",
						status === "current"
							? "text-gray-600"
							: "text-gray-400",
					)}
				>
					<p>
						<Trans
							i18nKey="investor:identity.copy.mangopay.timeline.kyc.description"
							components={{
								/* oxlint-disable jsx-a11y/anchor-has-content */
								afm: (
									<a
										className="text-secondary-700 font-medium hover:underline"
										href={EXTERNAL_PLATFORM_AFM_LICENSE_URL}
										target="_blank"
										rel="noreferrer"
									/>
								),
								/* oxlint-enable jsx-a11y/anchor-has-content */
							}}
						/>
					</p>
					<p>
						{t(
							"investor:identity.copy.mangopay.timeline.kyc.email",
						)}
					</p>
					<p>
						<Trans
							i18nKey="investor:identity.copy.mangopay.timeline.kyc.help"
							components={{
								/* oxlint-disable jsx-a11y/anchor-has-content */
								mail: (
									<a
										className="text-secondary-700 font-medium hover:underline"
										href="mailto:info@zonhub.com"
									/>
								),
								faq: (
									<a
										className="text-secondary-700 font-medium hover:underline"
										href={EXTERNAL_PLATFORM_ZONHUB_FAQ_URL}
										target="_blank"
										rel="noreferrer"
									/>
								),
								/* oxlint-enable jsx-a11y/anchor-has-content */
							}}
						/>
					</p>
				</div>

				{status === "current" && usesHostedKyc && (
					<div className="mt-3">
						<HostedVerificationAction />
					</div>
				)}
			</div>
		</li>
	)
}

const HostedVerificationAction = () => {
	const t = useTrans("investor")
	// The return path from Mangopay is a full-page navigation via `redirect()`,
	// so the SPA reboots and this query fetches once on mount. horizon delays
	// the IDV webhook by ~30s (horizon/webhooks/views.py), so that first fetch
	// reliably lands too early. Poll while the session is non-terminal to pick
	// up the outcome without a manual reload.
	const { data } = useUserKycStatusQuery(undefined, {
		refetchInterval: (query) => {
			const polledKyc = query.state.data?.me?.kyc
			if (polledKyc?.state === RiskKycStateChoices.VerificationSuccess) {
				return false
			}
			const polledStatus =
				polledKyc?.identity_verification?.status?.toUpperCase() ?? null
			return polledStatus === HOSTED_STATUS.PENDING ||
				polledStatus === HOSTED_STATUS.REVIEW ||
				polledStatus === HOSTED_STATUS.VALIDATED
				? HOSTED_POLL_INTERVAL_MS
				: false
		},
	})
	const queryClient = useQueryClient()
	const { redirect } = useContext(RedirectContext)
	const { addToast } = useToasts()

	const kyc = data?.me?.kyc
	const session = kyc?.identity_verification ?? null
	// Backend may return status in mixed case (e.g. "pending"); normalize to
	// the Mangopay-standard uppercase used across the docs and our constants.
	const hostedStatus = (session?.status?.toUpperCase() ??
		null) as HostedStatusKey | null

	// The outer step gate reads `kyc.state` from UserContext (a different
	// query). Invalidate it in parallel so the step advances to `complete`
	// once the polled session reaches VALIDATED and the backend has flipped
	// kyc.state.
	const isPolling =
		kyc?.state !== RiskKycStateChoices.VerificationSuccess &&
		(hostedStatus === HOSTED_STATUS.PENDING ||
			hostedStatus === HOSTED_STATUS.REVIEW ||
			hostedStatus === HOSTED_STATUS.VALIDATED)
	useEffect(() => {
		if (!isPolling) return
		const interval = setInterval(() => {
			void queryClient.invalidateQueries({ queryKey: ["UserContext"] })
		}, HOSTED_POLL_INTERVAL_MS)
		return () => clearInterval(interval)
	}, [isPolling, queryClient])

	const { mutate: startIdv, isPending: isStarting } =
		useIdentityVerificationStartMutation()
	const { mutate: startScaEnrollment, isPending: isStartingSca } =
		useScaEnrollmentStartMutation()

	const startSession = () => {
		// return_url is capped at 50 chars server-side and would blow the cap
		// on preview hosts (~71 chars) or on any prod URL that carries a query
		// string. Omit it and let the backend redirect to its default KYC
		// dashboard page after Mangopay's session.
		startIdv(
			{},
			{
				onSuccess: (result: IdentityVerificationStartMutation) => {
					const payload = result?.identity_verification_start
					if (payload?.sca_required) {
						// Contract: sca_required means chain SCA enrollment
						// first, then the user comes back and starts IDV.
						startScaEnrollment(
							{},
							{
								onSuccess: (
									scaResult: ScaEnrollmentStartMutation,
								) => {
									const scaUrl =
										scaResult?.sca_enrollment_start
											?.redirect_url
									if (!scaUrl) {
										addToast({
											variant: "alert",
											id: "hosted-idv-sca-unavailable",
											text: t(
												"investor:identity.hosted.start.error.sca_required",
											),
										})
										return
									}
									redirect(scaUrl)
								},
								onError: (error: unknown) => {
									captureMessage(
										"Hosted IDV SCA enrollment failed",
										{
											level: "error",
											extra: { error },
										},
									)
									addToast({
										variant: "alert",
										id: "hosted-idv-sca-error",
										text: t(
											"investor:identity.hosted.start.error.sca_required",
										),
									})
								},
							},
						)
						return
					}
					const url = payload?.hosted_url
					if (!url) {
						addToast({
							variant: "alert",
							id: "hosted-idv-unavailable",
							text: t(
								"investor:identity.hosted.start.error.unavailable",
							),
						})
						return
					}
					redirect(url)
				},
				onError: (error: unknown) => {
					captureMessage("Hosted IDV start failed", {
						level: "error",
						extra: { error },
					})
					addToast({
						variant: "alert",
						id: "hosted-idv-error",
						text: t("investor:identity.hosted.start.error.generic"),
					})
				},
			},
		)
	}

	const isBusy = isStarting || isStartingSca

	if (hostedStatus === HOSTED_STATUS.PENDING && session) {
		return (
			<PendingAction
				hostedUrl={session.hosted_url}
				createdAt={session.created_at}
			/>
		)
	}

	// VALIDATED can briefly precede kyc.state flipping to VerificationSuccess
	// (they come from separate queries). Show the review copy in that window
	// so the user doesn't see a start CTA and burn a fresh billed session.
	if (
		hostedStatus === HOSTED_STATUS.REVIEW ||
		hostedStatus === HOSTED_STATUS.VALIDATED
	) {
		return <ReviewNotice />
	}

	if (hostedStatus && STATUSES_NEEDING_RETRY.includes(hostedStatus)) {
		return (
			<RetryAction
				status={hostedStatus}
				refusalReasons={session?.refusal_reasons ?? null}
				onRetry={startSession}
				isRetrying={isBusy}
			/>
		)
	}

	return (
		<div>
			<img
				src="/assets/partners/mangopay_logo.png"
				alt="Mangopay logo"
				className="mb-3 w-28"
			/>
			<Button onClick={startSession} disabled={isBusy}>
				{t("investor:identity.hosted.start.button")}
			</Button>
		</div>
	)
}

const PendingAction = ({
	hostedUrl,
	createdAt,
}: {
	hostedUrl: string
	createdAt: string
}) => {
	const t = useTrans("investor")
	const DateTime = useLocalizedDateTime()
	// Refresh the countdown every 5 minutes — session lives 7 days so we don't
	// need per-second precision, but 15 min (the default) can make a
	// "yesterday"→"today" transition feel stale.
	const now = useCurrentDate(ms("5m"))
	const created = DateTime.fromISO(createdAt)
	const expiresAt = created.plus({ milliseconds: SESSION_TTL_MS })
	const remaining = expiresAt.toRelative({ base: now })

	return (
		<div className="space-y-2">
			<p className="text-sm text-gray-500">
				{remaining && expiresAt > now
					? t(
							"investor:identity.hosted.status.pending.validity_notice_with_time",
							// `toRelative` returns "in 6 days" / "over 6 dagen"; we
							// strip the leading preposition so the surrounding
							// sentence reads naturally in each locale.
							{ time: stripRelativePrefix(remaining) },
						)
					: t(
							"investor:identity.hosted.status.pending.validity_notice",
						)}
			</p>
			<ButtonLink
				href={hostedUrl}
				target="_blank"
				rel="noopener noreferrer"
			>
				{t("investor:identity.hosted.status.pending.continue")}
			</ButtonLink>
		</div>
	)
}

const ReviewNotice = () => {
	const t = useTrans("investor")
	return (
		<p className="text-sm text-gray-600">
			{t("investor:identity.hosted.status.review.copy")}
		</p>
	)
}

const RetryAction = ({
	status,
	refusalReasons,
	onRetry,
	isRetrying,
}: {
	status: string
	refusalReasons: Array<string | null> | null
	onRetry: () => void
	isRetrying: boolean
}) => {
	const t = useTrans("investor")
	const key =
		status === HOSTED_STATUS.EXPIRED
			? "expired"
			: status === HOSTED_STATUS.OUT_OF_DATE
				? "out_of_date"
				: "refused"
	const reasons = (refusalReasons ?? []).filter(
		(r): r is string => typeof r === "string" && r.length > 0,
	)

	return (
		<div className="space-y-3">
			<p className="text-sm text-gray-600">
				<Trans
					i18nKey={`investor:identity.hosted.status.${key}.copy`}
				/>
			</p>
			{reasons.length > 0 && (
				<div className="space-y-1">
					<p className="text-xs font-semibold text-gray-700">
						{t(
							"investor:identity.hosted.status.refused.reasons_heading",
						)}
					</p>
					<ul className="ml-4 list-disc text-xs text-gray-600">
						{reasons.map((reason) => (
							<li key={reason}>{reason}</li>
						))}
					</ul>
				</div>
			)}
			<Button onClick={onRetry} disabled={isRetrying}>
				{t(`investor:identity.hosted.status.${key}.retry`)}
			</Button>
		</div>
	)
}

// Luxon's toRelative returns e.g. "in 6 days" / "over 6 dagen" / "dans 6 jours".
// Our copy already carries the preposition ("Time remaining to complete:",
// "Nog {{ time }} om af te ronden.", "Il reste {{ time }} pour terminer.") so
// we drop the leading word.
function stripRelativePrefix(relative: string): string {
	return relative.replace(/^(in|over|dans)\s+/i, "")
}

const StepIndicator = ({
	stepNumber,
	status,
}: {
	stepNumber: number
	status: KycStepStatus
}) => {
	const base =
		"relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium ring-4 ring-white"

	if (status === "complete") {
		return (
			<span
				className={cn(
					base,
					"border-2 border-gray-200 bg-white text-gray-400",
				)}
				aria-hidden="true"
			>
				<CheckIcon className="size-4" />
			</span>
		)
	}

	if (status === "current") {
		return (
			<span
				className={cn(
					base,
					"border-secondary-500 text-secondary-700 border-2 bg-white",
				)}
				aria-hidden="true"
			>
				{stepNumber}
			</span>
		)
	}

	return (
		<span
			className={cn(
				base,
				"border-2 border-gray-200 bg-white text-gray-400",
			)}
			aria-hidden="true"
		>
			{stepNumber}
		</span>
	)
}

const StatusPill = ({
	status,
	label,
}: {
	status: KycStepStatus
	label: string
}) => {
	const styles =
		status === "current"
			? "bg-yellow-50 text-yellow-800"
			: "bg-gray-100 text-gray-500"
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
				styles,
			)}
		>
			{label}
		</span>
	)
}
